import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui';
import { ChevronLeft, Code2, Copy } from 'lucide-react';
import { FieldValue } from '../field-value';
import { useFieldPeekPanel } from './use-field-peek-panel';

export function FieldPeekPanel(props: Parameters<typeof useFieldPeekPanel>[0]) {
  const f = useFieldPeekPanel(props);

  return (
    <Sheet open={f.open} onOpenChange={f.onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2">
            {f.canGoBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={f.handleGoBack}
                className="h-8 w-8 p-0"
                title="Go back"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <div className="flex-1">
              <SheetTitle>Field: {f.currentItem.path}</SheetTitle>
              <SheetDescription>{f.descriptionLabel}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-2">
            <Button
              variant={!f.viewAsJson ? 'default' : 'outline'}
              size="sm"
              onClick={f.setViewStructured}
            >
              Structured View
            </Button>
            <Button
              variant={f.viewAsJson ? 'default' : 'outline'}
              size="sm"
              onClick={f.setViewJson}
              className="flex items-center gap-2"
            >
              <Code2 className="h-4 w-4" />
              View as JSON
            </Button>
          </div>

          {f.viewAsJson ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">JSON</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={f.handleCopyJson}
                  className="flex items-center gap-2"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
              </div>
              {f.copied && <p className="text-xs text-muted-foreground">Copied!</p>}
              <pre className="p-4 bg-muted rounded text-sm font-mono overflow-auto max-h-[60vh]">
                {JSON.stringify(f.currentItem.value, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="space-y-4">
              {f.showArrayView && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Array Items</h3>
                  <div className="space-y-2">
                    {f.arrayEntries.map(({ index, value, path, isNestedClickable }) => (
                      <div key={index} className="border rounded p-3 bg-card space-y-1">
                        <div className="text-xs font-mono text-muted-foreground">[{index}]</div>
                        <FieldValue
                          value={value}
                          maxLength={100}
                          onPeek={
                            isNestedClickable
                              ? (val, type) => f.handlePeek(val, type, path)
                              : undefined
                          }
                        />
                      </div>
                    ))}
                    {f.arrayEntries.length === 0 && (
                      <p className="text-sm text-muted-foreground">Empty array</p>
                    )}
                  </div>
                </div>
              )}
              {f.showMapView && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Map Fields</h3>
                  <div className="space-y-2">
                    {f.mapEntries.map(({ key, value, path, isNestedClickable }) => (
                      <div key={key} className="border rounded p-3 bg-card space-y-1">
                        <div className="text-xs font-mono text-muted-foreground">{key}</div>
                        <FieldValue
                          value={value}
                          maxLength={100}
                          onPeek={
                            isNestedClickable
                              ? (nestedVal, type) => f.handlePeek(nestedVal, type, path)
                              : undefined
                          }
                        />
                      </div>
                    ))}
                    {f.mapEntries.length === 0 && (
                      <p className="text-sm text-muted-foreground">Empty map</p>
                    )}
                  </div>
                </div>
              )}
              {!f.showArrayView && !f.showMapView && (
                <p className="text-sm text-muted-foreground">No data to display</p>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
